import React, { useEffect, useMemo, useState } from 'react';
import { FO_MENU_GROUPS, FO_RESERVATION_GROUP, FO_ALL_KEYS } from '../utils/frontOfficeMenus';

const STATIC_ROLE_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'admin', label: 'Admin (Global)' },
  { value: 'hotel_admin', label: 'Hotel Admin' },
  { value: 'operation_user', label: 'Operation User' },
  { value: 'bar_manager', label: 'Bar Manager' },
  { value: 'bar_cashier', label: 'Bar Cashier' },
  { value: 'purchase_manager', label: 'Purchase Manager' },
  { value: 'purchase_staff', label: 'Purchase Staff' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'store_keeper', label: 'Store Keeper' },
  { value: 'banquet_manager', label: 'Banquet Manager' },
  { value: 'banquet_staff', label: 'Banquet Staff' },
  { value: 'front_office_manager', label: 'Front Office Manager' },
  { value: 'front_office_staff', label: 'Front Office Staff' },
  { value: 'housekeeping_manager', label: 'Housekeeping Manager' },
  { value: 'housekeeping_staff', label: 'Housekeeping Staff' },
  { value: 'reservation_agent', label: 'Reservation Agent' },
  { value: 'kot_staff', label: 'KOT Staff' },
  { value: 'steward', label: 'Steward' },
  { value: 'kitchen_chef', label: 'Kitchen Chef' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'account_staff', label: 'Account Staff' }
];

const basePermissions = () => ({
  dashboard: false,
  bar: false,
  kot: false,
  banquet: false,
  front_office: false,
  housekeeping: false,
  hotel_admin: false,
  masters: false,
  food_kitchen_staff: false,
  food_storekeeper: false,
  food_store_receive: false,
  food_store_issue: false,
  food_store_manager: false,
  food_purchase_staff: false,
  food_purchase_manager: false,
  food_cost_controller: false,
  food_admin: false,
  ai_functions: false,
  maintenance: false,
  accounting: false,
  stores: false
});

const allTruePermissions = () => Object.keys(basePermissions()).reduce((acc, key) => ({ ...acc, [key]: true }), {});

const STATIC_ROLE_TEMPLATES = {
  staff: { ...basePermissions(), dashboard: true, bar: true },
  admin: allTruePermissions(),
  hotel_admin: allTruePermissions(),
  // Day-to-day operations across the guest journey (front office, housekeeping,
  // F&B service, banquet) — deliberately excludes hotel_admin, masters,
  // accounting, ai_functions and maintenance. Global Settings / API URL
  // Configuration screens (Channel Manager Settings etc.) are gated on
  // role === 'admin' directly, not on any permission flag, so a non-admin
  // role like this one is structurally locked out of them regardless of
  // which can_* flags it carries.
  operation_user: { ...basePermissions(), dashboard: true, front_office: true, housekeeping: true, kot: true, banquet: true, bar: true },
  bar_manager: { ...basePermissions(), dashboard: true, bar: true, masters: true },
  bar_cashier: { ...basePermissions(), dashboard: true, bar: true },
  purchase_manager: { ...basePermissions(), dashboard: true, food_purchase_manager: true, stores: true },
  purchase_staff: { ...basePermissions(), dashboard: true, food_purchase_staff: true, stores: true },
  store_manager: { ...basePermissions(), dashboard: true, food_store_manager: true, food_storekeeper: true, food_store_receive: true, food_store_issue: true, stores: true },
  store_keeper: { ...basePermissions(), dashboard: true, food_storekeeper: true, food_store_receive: true, food_store_issue: true, stores: true },
  steward: { ...basePermissions(), kot: true },
  kot_staff: { ...basePermissions(), dashboard: true, kot: true },
  kitchen_chef: { ...basePermissions(), dashboard: true, kot: true, food_kitchen_staff: true },
  banquet_manager: { ...basePermissions(), dashboard: true, banquet: true, masters: true },
  banquet_staff: { ...basePermissions(), banquet: true },
  front_office_manager: { ...basePermissions(), dashboard: true, front_office: true, masters: true },
  front_office_staff: { ...basePermissions(), front_office: true },
  housekeeping_manager: { ...basePermissions(), dashboard: true, housekeeping: true, masters: true },
  housekeeping_staff: { ...basePermissions(), housekeeping: true },
  reservation_agent: { ...basePermissions(), front_office: true },
  account_manager: { ...basePermissions(), dashboard: true, masters: true, accounting: true },
  account_staff: { ...basePermissions(), masters: true, accounting: true }
};

const normalizeRoleCode = (value) => String(value || '').trim().toLowerCase();

function Users({ token, selectedPropertyId }) {
  const [roleTemplates, setRoleTemplates] = useState([]);

  const dynamicRoleOptions = useMemo(() => {
    const dynamic = roleTemplates.map((row) => ({
      value: String(row.role_code || '').trim().toLowerCase(),
      label: String(row.role_name || row.role_code || '').trim(),
      permissions: { ...basePermissions(), ...(row.permissions_json || {}) }
    }));
    const merged = [...STATIC_ROLE_OPTIONS];
    dynamic.forEach((opt) => {
      if (!opt.value) return;
      if (!merged.some((existing) => existing.value === opt.value)) merged.push({ value: opt.value, label: opt.label });
    });
    return { merged, dynamic };
  }, [roleTemplates]);

  const roleTemplatesByCode = useMemo(() => {
    const map = {};
    dynamicRoleOptions.dynamic.forEach((opt) => {
      map[opt.value] = opt.permissions;
    });
    return map;
  }, [dynamicRoleOptions]);

  const getRoleTemplatePermissions = (roleCode) => {
    const normalizedRole = normalizeRoleCode(roleCode || 'staff');
    if (roleTemplatesByCode[normalizedRole]) return { ...basePermissions(), ...roleTemplatesByCode[normalizedRole] };
    if (STATIC_ROLE_TEMPLATES[normalizedRole]) return { ...basePermissions(), ...STATIC_ROLE_TEMPLATES[normalizedRole] };
    return { ...basePermissions() };
  };

  const createEmptyForm = () => ({
    username: '',
    mobile_number: '',
    password: '',
    role: 'staff',
    property_ids: [],
    is_active: true,
    can_cm: false,
    fo_menu_access: null,
    permissions: getRoleTemplatePermissions('staff')
  });

  const emptyForm = {
    username: '',
    mobile_number: '',
    password: '',
    role: 'staff',
    property_ids: [],
    is_active: true,
    can_cm: false,
    permissions: getRoleTemplatePermissions('staff')
  };

  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [selectedTemplateCode, setSelectedTemplateCode] = useState('');

  const loadUsers = () => {
    fetch('/api/users', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setError('Failed to load users'));
  };

  const loadProperties = () => {
    fetch('/api/properties/accessible', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]));
  };

  const loadRoleTemplates = () => {
    if (!selectedPropertyId) {
      setRoleTemplates([]);
      return;
    }
    fetch(`/api/role-templates?property_id=${encodeURIComponent(selectedPropertyId)}&all=1`, {
      headers: { Authorization: `Bearer ${token}`, 'x-property-id': selectedPropertyId }
    })
      .then((res) => res.json())
      .then((data) => setRoleTemplates(Array.isArray(data) ? data.filter((r) => r?.is_active !== false) : []))
      .catch(() => setRoleTemplates([]));
  };

  useEffect(() => {
    loadUsers();
    loadProperties();
  }, [token]);

  useEffect(() => {
    loadRoleTemplates();
  }, [token, selectedPropertyId]);

  const handleRoleChange = value => {
    const selectedRole = normalizeRoleCode(value || 'staff');
    const template = getRoleTemplatePermissions(selectedRole);
    setForm({
      ...form,
      role: selectedRole,
      permissions: { ...template }
    });
  };

  const handlePermissionChange = (key, value) => {
    setForm({ ...form, permissions: { ...form.permissions, [key]: value } });
  };

  // Front Office Menus: null = every menu, an array = only those screens.
  const toggleFoMenus = (keys, on) => {
    const current = form.fo_menu_access || FO_ALL_KEYS;
    const next = on ? [...new Set([...current, ...keys])] : current.filter((k) => !keys.includes(k));
    setForm({ ...form, fo_menu_access: next });
  };

  const applyTemplateToForm = (templateCode) => {
    const normalized = normalizeRoleCode(templateCode);
    if (!normalized) return;
    const template = getRoleTemplatePermissions(normalized);
    setForm((prev) => ({
      ...prev,
      role: normalized,
      permissions: { ...template }
    }));
  };

  // The limit only applies to Front Office users who are not admins.
  const canLimitFoMenus = form.role !== 'admin' && form.role !== 'hotel_admin' && !form.permissions.hotel_admin && Boolean(form.permissions.front_office);
  const isFoMenuLimited = canLimitFoMenus && Array.isArray(form.fo_menu_access);

  const handleCreateOrUpdate = async () => {
    const normalizedUsername = (form.username || '').trim();
    const normalizedMobile = (form.mobile_number || '').trim();

    if (!normalizedUsername) {
      setError('Username is required');
      return;
    }
    // Mobile is optional; when entered it must be 10 digits (used for login / OTP).
    if (normalizedMobile && !/^\d{10}$/.test(normalizedMobile)) {
      setError('Mobile number must be exactly 10 digits');
      return;
    }
    if (!editingId && !form.password) {
      setError('Password is required');
      return;
    }
    if (isFoMenuLimited && form.fo_menu_access.length === 0) {
      setError('Tick at least one Front Office menu, or choose All menus');
      return;
    }
    if (form.role !== 'admin' && (!Array.isArray(form.property_ids) || form.property_ids.length === 0)) {
      setError('At least one property is required for non-admin users');
      return;
    }

    const payload = {
      username: normalizedUsername,
      mobile_number: normalizedMobile,
      mobileNumber: normalizedMobile,
      role: form.role,
      property_ids: form.role === 'admin' ? [] : form.property_ids,
      is_active: form.is_active,
      can_cm: form.can_cm === true,
      fo_menu_access: isFoMenuLimited ? form.fo_menu_access : null,
      permissions: {
        ...form.permissions,
        dashboard: form.role === 'admin' ? true : Boolean(form.permissions.dashboard)
      }
    };
    if (form.password) payload.password = form.password;

    try {
      const res = await fetch(editingId ? `/api/users/${editingId}` : '/api/users', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Save failed');
        return;
      }

      setError('');
      setForm(createEmptyForm());
      setEditingId(null);
      setShowPassword(false);
      setSelectedTemplateCode('');
      loadUsers();
    } catch {
      setError('Network error while saving user');
    }
  };

  const handleEdit = user => {
    setEditingId(user.id);
    setShowPassword(false);
    setForm({
      username: user.username,
      mobile_number: user.mobile_number || '',
      password: '',
      role: normalizeRoleCode(user.role || 'staff'),
      property_ids: Array.isArray(user.property_ids)
        ? user.property_ids
        : String(user.property_id || '').split(',').map((v) => v.trim()).filter(Boolean),
      is_active: Boolean(user.is_active),
      can_cm: Boolean(user.can_cm),
      fo_menu_access: Array.isArray(user.fo_menu_access) ? user.fo_menu_access : null,
      permissions: {
        dashboard: Boolean(user.can_dashboard),
        bar: Boolean(user.can_bar ?? user.can_inventory),
        kot: Boolean(user.can_kot ?? user.can_orders),
        banquet: Boolean(user.can_banquet),
        front_office: Boolean(user.can_front_office),
        housekeeping: Boolean(user.can_housekeeping),
        hotel_admin: Boolean(user.can_hotel_admin),
        masters: Boolean(user.can_masters),
        food_kitchen_staff: Boolean(user.can_food_kitchen_staff ?? user.can_kot ?? user.can_orders),
        food_storekeeper: Boolean(user.can_food_storekeeper ?? user.can_kot ?? user.can_orders),
        food_store_receive: Boolean(user.can_food_store_receive ?? user.can_food_storekeeper ?? user.can_kot ?? user.can_orders),
        food_store_issue: Boolean(user.can_food_store_issue ?? user.can_food_storekeeper ?? user.can_kot ?? user.can_orders),
        food_store_manager: Boolean(user.can_food_store_manager ?? user.can_food_storekeeper ?? user.can_kot ?? user.can_orders),
        food_purchase_staff: Boolean(user.can_food_purchase_staff ?? user.can_kot ?? user.can_orders),
        food_purchase_manager: Boolean(user.can_food_purchase_manager ?? user.can_kot ?? user.can_orders),
        food_cost_controller: Boolean(user.can_food_cost_controller ?? user.can_kot ?? user.can_orders),
        food_admin: Boolean(user.can_food_admin),
        ai_functions: Boolean(user.can_ai_functions),
        maintenance: Boolean(user.can_maintenance),
        accounting: Boolean(user.can_accounting),
        stores: Boolean(user.can_stores)
      }
    });
    setError('');
    setSelectedTemplateCode('');
  };

  const handleDelete = async id => {
    try {
      const res = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setError('');
      loadUsers();
    } catch {
      setError('Network error');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(createEmptyForm());
    setShowPassword(false);
    setSelectedTemplateCode('');
  };

  const propertyNamesByIds = (ids) => {
    const list = Array.isArray(ids)
      ? ids
      : String(ids || '').split(',').map((v) => v.trim()).filter(Boolean);
    if (!list.length) return '-';
    return list
      .map((id) => {
        const match = properties.find((p) => String(p.id) === String(id));
        return match ? `${match.code} - ${match.name}` : id;
      })
      .join(', ');
  };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Users</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>User Management</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Create and manage staff accounts with role-based module access</p>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginTop: 0, color: 'var(--theme-text)', marginBottom: '1rem' }}>{editingId ? 'Edit User' : 'Create User'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', alignItems: 'start' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Username</label>
            <input
              placeholder="Username"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Mobile Number <span style={{ fontWeight: 400, color: 'var(--theme-text-muted, #64748b)' }}>(optional)</span></label>
            <input
              placeholder="10-digit mobile (needed for OTP login)"
              value={form.mobile_number}
              onChange={e => setForm({ ...form, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              inputMode="numeric"
              maxLength={10}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Password</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={editingId ? 'New Password (optional)' : 'Password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                style={{ width: '100%' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="btn-muted btn-sm"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Role</label>
            <select
              value={form.role}
              onChange={e => handleRoleChange(e.target.value)}
              style={{ width: '100%' }}
            >
              {dynamicRoleOptions.merged.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Apply Template</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center' }}>
              <select
                value={selectedTemplateCode}
                onChange={(e) => setSelectedTemplateCode(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Apply Role Template (optional)</option>
                {dynamicRoleOptions.dynamic.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => applyTemplateToForm(selectedTemplateCode)}
                disabled={!selectedTemplateCode}
                className={`btn-sm ${selectedTemplateCode ? 'btn-success' : 'btn-muted'}`}
              >
                Apply
              </button>
            </div>
          </div>
          <div style={{ alignSelf: 'start' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Assign Properties</label>
            <select
              multiple
              value={form.property_ids}
              onChange={e => setForm({
                ...form,
                property_ids: Array.from(e.target.selectedOptions || []).map((opt) => opt.value)
              })}
              size={Math.min(6, Math.max(3, properties.length || 3))}
              style={{ width: '100%', minHeight: '7.5rem' }}
            >
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Status</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
              Active User
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                checked={form.can_cm}
                onChange={e => setForm({ ...form, can_cm: e.target.checked })}
              />
              Channel Manager Access
            </label>
          </div>
          {form.role !== 'admin' && (
            <div style={{ fontSize: '0.85rem', color: '#636e72' }}>
              Hold Ctrl/Cmd to select multiple properties
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-body">
            <div className="section-head">Module Access</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.bar} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('bar', e.target.checked)} /> Bar Management</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.kot} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('kot', e.target.checked)} /> KOT</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.banquet} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('banquet', e.target.checked)} /> Banquet</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.front_office} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('front_office', e.target.checked)} /> Front Office</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.housekeeping} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('housekeeping', e.target.checked)} /> Housekeeping</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.hotel_admin} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('hotel_admin', e.target.checked)} /> Hotel Admin (All Ops in Assigned Hotel)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.masters} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('masters', e.target.checked)} /> Masters</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_kitchen_staff} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_kitchen_staff', e.target.checked)} /> Food - Kitchen Staff</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_store_receive} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_store_receive', e.target.checked)} /> Food - Store Receive (GRN)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_store_issue} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_store_issue', e.target.checked)} /> Food - Store Issue</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_store_manager} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_store_manager', e.target.checked)} /> Food - Store Manager (Approve)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_purchase_staff} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_purchase_staff', e.target.checked)} /> Food - Purchase Staff</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_purchase_manager} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_purchase_manager', e.target.checked)} /> Food - Purchase Manager</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_cost_controller} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_cost_controller', e.target.checked)} /> Food - Cost Controller</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.food_admin} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('food_admin', e.target.checked)} /> Food - Admin</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.accounting} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('accounting', e.target.checked)} /> Accounting</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.ai_functions} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('ai_functions', e.target.checked)} /> AI Functions</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }}><input type="checkbox" checked={form.permissions.maintenance} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('maintenance', e.target.checked)} /> Maintenance / Engineering</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.875rem', cursor: 'pointer' }} title="What they can do in Stores is set in Stores → Settings & Access"><input type="checkbox" checked={form.permissions.stores} disabled={form.role === 'admin'} onChange={e => handlePermissionChange('stores', e.target.checked)} /> Stores</label>
            </div>
          </div>
        </div>

        {canLimitFoMenus && (
          <div className="card" style={{ marginTop: '1rem' }}>
            <div className="card-body">
              <div className="section-head">Front Office Menus</div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" checked={!isFoMenuLimited} onChange={() => setForm({ ...form, fo_menu_access: null })} /> All menus
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" checked={isFoMenuLimited} onChange={() => setForm({ ...form, fo_menu_access: form.fo_menu_access || ['pms-reservations', ...FO_RESERVATION_GROUP.items.map((i) => i.key)] })} /> Only the menus ticked below
                </label>
              </div>
              {isFoMenuLimited && (
                <>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                    The user sees only these Front Office menus. Example, sales team: tick Reservations only. Under Reservation Screen, tick the statuses they may set (none ticked = all) and whether they see the Recent Reservations list.
                    <button type="button" className="btn-muted btn-sm" style={{ marginLeft: '0.75rem' }} onClick={() => toggleFoMenus(FO_ALL_KEYS, true)}>Select all</button>
                    <button type="button" className="btn-muted btn-sm" style={{ marginLeft: '0.4rem' }} onClick={() => setForm({ ...form, fo_menu_access: [] })}>Unselect all</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    {[...FO_MENU_GROUPS, FO_RESERVATION_GROUP].map((group) => {
                      const keys = group.items.map((i) => i.key);
                      const allOn = keys.every((k) => form.fo_menu_access.includes(k));
                      return (
                        <div key={group.label} style={{ border: '1px solid #e2e8f0', borderRadius: 6 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.5rem', background: '#f1f5f9', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={allOn} onChange={(e) => toggleFoMenus(keys, e.target.checked)} /> {group.label}
                          </label>
                          {group.items.map((item) => (
                            <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.5rem 0.25rem 1.4rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={form.fo_menu_access.includes(item.key)} onChange={(e) => toggleFoMenus([item.key], e.target.checked)} /> {item.label}
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleCreateOrUpdate} className="btn-primary" style={{ padding: '0.65rem 1.4rem' }}>
            {editingId ? 'Update User' : 'Create User'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.65rem 1.4rem' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3 style={{ color: 'var(--theme-primary-dark)', marginTop: '1.5rem', marginBottom: '0.75rem' }}>Users</h3>
      {users.length === 0 ? (
        <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#fafafa', borderRadius: '8px', border: '1px dashed var(--theme-border)' }}>No users found. Create your first user above.</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto', marginTop: '0.25rem' }}>
          <table>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th>ID</th>
                <th>Username</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Properties</th>
                <th>Modules</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.mobile_number || '-'}</td>
                  <td>{user.role}</td>
                  <td>{propertyNamesByIds(user.property_ids || user.property_id)}</td>
                  <td>
                    {[
                      user.can_bar ? 'Bar' : null,
                      user.can_kot ? 'KOT' : null,
                      user.can_banquet ? 'Banquet' : null,
                      user.can_front_office ? 'Front Office' : null,
                      user.can_housekeeping ? 'Housekeeping' : null,
                      user.can_hotel_admin ? 'Hotel Admin' : null,
                      user.can_masters ? 'Masters' : null,
                      user.can_food_kitchen_staff ? 'Food Kitchen' : null,
                      user.can_food_store_receive ? 'Food Store Receive' : null,
                      user.can_food_store_issue ? 'Food Store Issue' : null,
                      user.can_food_store_manager ? 'Food Store Manager' : null,
                      user.can_food_storekeeper ? 'Food Storekeeper' : null,
                      user.can_food_purchase_staff ? 'Food Purchase Staff' : null,
                      user.can_food_purchase_manager ? 'Food Purchase' : null,
                      user.can_food_cost_controller ? 'Food Cost Ctrl' : null,
                      user.can_food_admin ? 'Food Admin' : null,
                      user.can_ai_functions ? 'AI Functions' : null,
                      user.can_maintenance ? 'Maintenance' : null,
                      user.can_stores ? 'Stores' : null
                    ].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleEdit(user)} className="btn-primary btn-sm">Edit</button>
                    <button onClick={() => handleDelete(user.id)} className="btn-danger btn-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Users;
